'use client';

import { variables } from './variables';
import { createTheme, Shadows } from '@mui/material/styles';
import type {} from '@mui/lab/themeAugmentation';
import { createBreakpoints } from '@mui/system';
import sharedTheme from '../shared/theme';

const { colors, fonts, borderRadius, padding, boxShadows, typography } = variables;

const breakpoints = createBreakpoints({});

const defaultTheme = sharedTheme(variables);

const outlinedInputStyles = {
    styleOverrides: {
        root: {
            ...defaultTheme.MuiOutlinedInput.styleOverrides.root,
            borderRadius: borderRadius.primary,

            '&.Mui-disabled': {
                '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(243, 243, 243)',
                },
            },
        },
    },
};

export const theme = createTheme({
    colors,
    fonts,
    borderRadius,
    padding,
    palette: colors.palette,
    typography,
    components: {
        MuiAlert: {
            styleOverrides: {
                message: {
                    ...defaultTheme.MuiAlert.styleOverrides.message,
                    padding: '8px 0',
                },
                root: {
                    '&.notificationAlert': {
                        border: `1px solid ${colors.other.text.lightGray}`,
                        position: 'relative',
                    },
                    '&.notificationAlert > ::after': {
                        display: 'block',
                        content: "''",
                        position: 'absolute',
                        top: '-1px',
                        height: '1px',
                        backgroundColor: colors.other.notifications.success,
                        left: '9px',
                        right: '9px',
                        borderRadius: '12px',
                    },
                    '&.notificationAlertWarning ::after': {
                        backgroundColor: `${colors.other.notifications.warning}`,
                    },
                    '&.notificationAlertError ::after': {
                        backgroundColor: `${colors.other.notifications.error}`,
                    },
                    '&.notificationAlertInfo ::after': {
                        backgroundColor: `${colors.other.notifications.info}`,
                    },
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    '&.homePageTitle': {
                        fontSize: '2rem',
                        fontWeight: 700,
                        textTransform: 'none',
                    },
                    [breakpoints.up('lg')]: {
                        '&.homePageTitle': {
                            maxWidth: '70%',
                        },
                    },
                    '&.homePageDescription': {
                        color: colors.palette.secondary.main,
                        fontSize: '1rem',
                        fontWeight: 400,
                        marginTop: 0,
                    },
                    '&.badgeDefinitionTitle, &.introTitle': {
                        fontSize: '1.125rem',
                        textTransform: 'none',
                        margin: '8px auto',
                    },
                    '&.badgeDefinitionDescription, &.introDescription': {
                        fontFamily: fonts.secondary,
                        fontSize: '0.875rem',
                        fontWeight: 400,
                        color: colors.palette.text.primary,
                        textTransform: 'none',
                    },
                    '&.nmDashboardWelcomeTitle': {
                        textTransform: 'lowercase',
                        '&:first-letter': {
                            textTransform: 'uppercase',
                        },
                    },
                    '&.nmDashboardNote': {
                        fontSize: '0.75rem',
                        color: 'rgba(127, 136, 152, 1)',
                    },
                    '&.nmSurveyCardLabel': {
                        fontWeight: 600,
                        color: 'rgba(127, 136, 152, 1)',
                    },
                    '&.surveyDetailsValue': {
                        color: colors.palette.secondary.main,
                        textTransform: 'none',
                        marginRight: '16px',
                        fontWeight: 600,
                    },
                    '&.surveyDetailsValue span': {
                        color: 'rgba(111, 209, 132)',
                    },
                    '&.dynamicStaticPagesLink': {
                        color: colors.palette.primary.main,
                        fontWeight: 500,
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                ...defaultTheme.MuiCssBaseline.styleOverrides,
                'h1, h2, h3, h4, h5, h6, h7': {
                    color: colors.palette.secondary.main,
                },
                'main .introRegistrationWrapper': {
                    paddingBottom: 0,
                },
                '&.Header .HeaderImage, &.Header .HeaderImage .HeaderLogo a, &.Header .HeaderImage .HeaderLogo img':
                    {
                        height: '52px',
                    },
                '&.Footer': {
                    borderTop: `1px solid rgba(206, 212, 218)`,
                },
                '&.FooterLogo': {
                    marginBottom: '8px',
                },
                '&.Footer .FooterLogo img': {
                    height: '40px',
                },
                'table td': {
                    transition: 'all 0.15s',
                    padding: padding.tableCellPadding,
                },
                'table tr:hover td': {
                    color: colors.palette.primary.main,
                    background: 'inherit',
                },
                'table tr:nth-of-type(even):hover td': {
                    background: colors.other.table.evenRowHighlight,
                },
                '.introTextBlockWithPic': {
                    [breakpoints.up('md')]: {
                        paddingLeft: '304px',
                        position: 'relative',
                        mb: 2,
                    },
                },
                '&.triangleArrowUp': {
                    borderBottom: `5px solid ${colors.other.text.header}`,
                },
                '&.triangleArrowDown': {
                    borderTop: `5px solid ${colors.other.text.header}`,
                },
                '.topMenuLinksList': {
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                },
                '&.verificationFormElementBlock ': {
                    paddingRight: '8px',
                    paddingLeft: '8px',
                },
                '&.verificationSubmitButtonWrapper': {
                    paddingRight: '8px',
                },
                '&.paymentMethodComingSoonLabel': {
                    color: colors.other.text.info,
                    background: 'rgb(229, 246, 253)',
                    padding: '2px 6px',
                    top: '0',
                    right: '20px',
                },
                '&.referralInfoWrapper': {
                    paddingLeft: '8px',
                    paddingTop: '8px',
                },
                '&.WalletMinMaxLabel': {
                    backgroundColor: colors.other.background.highlight,
                    color: colors.other.text.header,
                    borderRadius: borderRadius.tertiary,
                },
                '.Header': {
                    backdropFilter: 'none',
                },
                '.HeaderLogoNewYearAnimation': {
                    width: '250px',
                },
                '.AccountUserIdBlock ': {
                    backgroundColor: colors.other.background.header,
                    borderRadius: borderRadius.secondary,
                },
                '.AccountUserId': {
                    fontSize: '16px',
                    fontWeight: '600',
                    color: colors.palette.text.primary,
                },
                '&.introIcons': {
                    backgroundColor: 'transparent',
                },
                '&.MuiPaper-root.infoBox': {
                    border: 'none',
                    backgroundColor: 'transparent',
                    paddingTop: '32px',
                },
                '&.introStack': {
                    justifyContent: 'space-between',
                    marginTop: '-12px',
                },
                '&.introStack .homeIntroIconsItem': {
                    flexBasis: '100%',
                    maxWidth: '100%',
                    border: `1px solid ${colors.palette.border.main}`,
                    borderRadius: borderRadius.secondary,
                    background: 'white',
                    margin: '12px 0 0',
                },
                '&.ForgotPasswordLink': {
                    fontSize: '0.875rem',
                    fontWeight: '500',
                },
                [breakpoints.up('md')]: {
                    '&.introStack': {
                        marginTop: '-24px',
                    },
                    '&.introStack .homeIntroIconsItem': {
                        flexBasis: 'calc(33.3% - 15px)',
                        maxWidth: 'calc(33.3% - 15px)',
                        marginTop: '24px',
                    },
                },
                '.socialIconLink': {
                    color: colors.links.main,
                    border: `1px solid ${colors.links.main}`,
                },
                '&.Header .HeaderPanelImageLogo, &.Header .HeaderPanelImageLogo img': {
                    height: '50px',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiPaper.styleOverrides.root,
                    border: `1px solid ${colors.palette.border.main}`,
                    '&.warningBox': {
                        border: `1px solid ${colors.other.text.warning}`,
                        h3: {
                            color: colors.palette.secondary.main,
                        },
                    },
                    '&.lang-switcher-wrapper': {
                        maxHeight: 'calc(100% - 32px)',
                    },
                    '&.accountMenuBox': {
                        padding: '8px',
                    },
                    '&.bottomPicBlock': {
                        padding: 0,
                        lineHeight: 0,
                        border: 0,
                        img: {
                            width: '100%',
                            height: 'auto',
                            lineHeight: 0,
                            borderRadius: borderRadius.secondary,
                            overflow: 'hidden',
                        },
                    },
                    '&.accountTaxDetails': {
                        '.helperText': {
                            position: 'relative',
                            borderRadius: '4px',
                            backgroundColor: colors.palette.background.default,
                            padding: '16px',
                            [breakpoints.up('md')]: {
                                marginLeft: '20px',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '15px',
                                    left: '-9px',
                                    width: '0',
                                    height: '0',
                                    borderTop: '10px solid transparent',
                                    borderBottom: '10px solid transparent',
                                    borderRight: `10px solid ${colors.palette.background.default}`,
                                    transform: 'translateY(-50%)',
                                },
                            },
                        },
                    },
                    '&.dynamicStaticPagesItem': {
                        transition: 'all 0.25s',
                    },
                    '&.dynamicStaticPagesItem:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.accountMenuWrapper': {
                        padding: 0,
                        border: 0,
                        background: 'transparent',
                    },
                    '&.infoCard': {
                        background: 'transparent',
                        border: 'none',
                        marginTop: '12px',
                        '.infoCardRow': {
                            justifyContent: 'space-between',
                        },
                        '.infoCardItem': {
                            flexBasis: '100%',
                            maxWidth: '100%',
                            marginTop: '12px',
                            border: `1px solid ${colors.palette.border.main}`,
                            background: 'white',
                            borderRadius: borderRadius.secondary,
                        },
                        [breakpoints.up('sm')]: {
                            '.infoCardItem': {
                                flexBasis: 'calc(50% - 6px)',
                                maxWidth: 'calc(50% - 6px)',
                            },
                        },
                        [breakpoints.up('lg')]: {
                            '.infoCardRow': {
                                marginTop: '24px',
                            },
                            '.infoCardItem': {
                                marginTop: 0,
                                flexBasis: 'calc(33.3% - 15px)',
                                maxWidth: 'calc(33.3% - 15px)',
                            },
                        },
                    },
                },
                elevation: {
                    borderRadius: borderRadius.secondary,
                },
                elevation1: {
                    borderRadius: borderRadius.secondary,
                },
            },
        },
        MuiPopover: defaultTheme.MuiPopover,
        MuiOutlinedInput: outlinedInputStyles,
        MuiPickersOutlinedInput: outlinedInputStyles,
        MuiCard: {
            styleOverrides: {
                root: {
                    '&.surveyCard h4': {
                        fontWeight: 700,
                    },
                    '&.introRegistration': {
                        ...defaultTheme.MuiCard.styleOverrides.root['&.introRegistration'],
                        position: 'relative',
                        padding: 0,
                        margin: 0,
                        width: '100%',
                        background: 'transparent',
                        borderRadius: '0',
                    },
                    '&.introRegistration .introTextBlock': {
                        display: 'none',
                    },
                    [breakpoints.up('lg')]: {
                        '&.introRegistration .introTextBlock': {
                            display: 'inline-flex',
                            width: '50%',
                            margin: '36px 0',
                            borderRadius: `${borderRadius.secondary} 0 0 ${borderRadius.secondary}`,
                            overflow: 'hidden',
                            background: `
                            url('/images/themes/medplanet/registration-intro-pic.jpg?new') left top / cover no-repeat`,
                        },
                    },
                    '.referral &': {
                        padding: variables.padding.cardPadding,
                    },
                    '.referralDetails': {
                        padding: '10px 0',
                    },
                    '.nmSurveyCardTitle': {
                        color: 'white',
                        background: colors.palette.secondary.main,
                    },
                    '.nmSurveyCardBaseInfo': {
                        background: colors.palette.secondary.main,
                    },
                    '.nmSurveyCardBaseInfo *': {
                        color: 'white',
                    },
                    '&.referralIntroWrapper': {
                        padding: '16px 24px 24px 24px',
                    },
                },
            },
        },
        MuiChip: defaultTheme.MuiChip,
        MuiAutocomplete: defaultTheme.MuiAutocomplete,
        MuiSelect: defaultTheme.MuiSelect,
        MuiAccordion: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiAccordion.styleOverrides.root,
                    borderRadius: borderRadius.primary,
                    boxShadow: 'none',
                    '&:first-of-type, &:last-of-type': {
                        borderRadius: borderRadius.primary,
                    },
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiList.styleOverrides.root,
                    a: {
                        width: '100%',
                        padding: '0 16px',
                    },
                    '&.topNavigationMenu': {
                        border: 'none',
                        backgroundColor: colors.other.background.header,
                        color: colors.palette.text.primary,
                        li: {
                            width: '100%',
                        },
                    },
                    '&.topNavigationMenu .selectOpinionTopMenuItem': {
                        display: 'flex',
                        gap: '0.75rem',
                        color: 'white',
                        borderRadius: borderRadius.primary,
                        backgroundColor: 'rgba(29, 81, 86, 1)',
                    },
                },
            },
        },
        MuiListSubheader: defaultTheme.MuiListSubheader,
        MuiMenu: defaultTheme.MuiMenu,
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiMenuItem.styleOverrides.root,
                    '& a': {
                        fontSize: '0.875rem',
                        color: colors.palette.text.primary,
                        fontWeight: 400,
                    },
                    '&.MuiButtonBase-root.Mui-selected': {
                        color: colors.palette.primary.main,
                        background: 'transparent',
                    },
                    '&:hover': {
                        color: colors.palette.primary.main,
                        backgroundColor: colors.palette.background.menuItemHover,
                    },
                    '&:not(.active):hover': {
                        backgroundColor: colors.palette.background.menuItemHover,
                        '& a': {
                            color: colors.palette.primary.main,
                            backgroundColor: 'white',
                        },
                    },
                    '&.accountMenuItem': {
                        padding: 0,
                        marginBottom: '8px',
                        '&:hover': {
                            backgroundColor: 'none',
                        },
                        '& a': {
                            padding: '12px 16px',
                        },
                        '&.active': {
                            '& a': {
                                backgroundColor: colors.palette.primary.main,
                                color: 'white',
                                fontWeight: 600,
                            },
                        },
                        '&:not(.active):hover': {
                            '& a': {
                                background: colors.other.background.highlight,
                                color: colors.palette.primary.main,
                            },
                        },
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginBottom: '10px',
                    '&.radioGroupThemeControl': {
                        transition: 'all 0.15s',
                        borderRadius: borderRadius.secondary,
                        border: `1px solid ${colors.palette.border.main}`,
                    },
                    '&.radioGroupThemeControl:hover': {
                        backgroundColor: colors.other.background.topMenu,
                        boxShadow: 'none',
                    },
                    '&.radioGroupThemeControl .MuiFormControlLabel-root.Mui-disabled, &.radioGroupThemeControl .MuiFormControlLabel-root.Mui-disabled span':
                        {
                            cursor: 'default',
                        },
                    '&.radioGroupControlActive': {
                        background: colors.other.background.highlight,
                        boxShadow: 'none',
                        borderRadius: borderRadius.secondary,
                    },
                    '&.radioGroupControlActive:hover': {
                        background: colors.other.background.highlight,
                    },
                    '.ProfileQuestionsContent &:has(:not([value=""]):not(:focus):invalid) .MuiInputLabel-root':
                        { color: colors.other.text.error },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiInputLabel.styleOverrides.root,
                    color: 'rgba(173, 181, 189)',
                    '&.Mui-focused': {
                        color: colors.palette.text.primary,
                    },
                    '&.MuiInputLabel-shrink': {
                        color: colors.palette.text.primary,
                        transform: 'translate(10px, -20px) scale(0.75)',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontFamily: fonts.additional,
                    '&.Mui-disabled': {
                        backgroundColor: 'rgba(243, 243, 243)',
                        borderColor: 'rgba(243, 243, 243)',
                    },
                },
            },
        },
        MuiFormControlLabel: defaultTheme.MuiFormControlLabel,
        MuiCheckbox: defaultTheme.MuiCheckbox,
        MuiRadio: defaultTheme.MuiRadio,
        MuiTouchRipple: defaultTheme.MuiTouchRipple,
        MuiFormHelperText: defaultTheme.MuiFormHelperText,
        MuiButton: {
            variants: [
                {
                    props: { variant: 'tertiary' },
                    style: {
                        height: '30px',
                        color: colors.palette.text.primary,
                        backgroundColor: 'transparent',
                        fontFamily: fonts.secondary,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        margin: '0 4px',
                        border: 'none',
                        '&:hover': {
                            backgroundColor: colors.other.background.highlight,
                            color: colors.palette.primary.main,
                            borderRadius: borderRadius.primary,
                        },
                        '&:active': {
                            backgroundColor: 'rgba(213, 231, 246, 1)',
                            color: colors.palette.primary.main,
                        },
                        '&.active.topMenuButton': {
                            color: colors.palette.primary.main,
                            backgroundColor: 'transparent',
                        },
                        '&:focus': {
                            backgroundColor: 'rgba(213, 231, 246, 1)',
                            color: colors.palette.primary.main,
                        },
                        '& .MuiTouchRipple-root': {
                            display: 'none',
                        },
                    },
                },
                {
                    props: { variant: 'descriptive' },
                    style: {
                        backgroundColor: colors.palette.background.default,
                        border: `1px solid ${colors.palette.border.main}`,
                        borderRadius: borderRadius.secondary,
                        '.title': {
                            color: colors.palette.primary.main,
                        },
                        '.description': {
                            color: colors.palette.text.primary,
                        },
                        '.chevron': {
                            color: colors.palette.text.primary,
                        },
                        '&:hover': {
                            color: colors.palette.primary.main,
                            boxShadow: boxShadows.descriptiveShadow,
                            border: `1px solid ${colors.palette.primary.main}`,
                            backgroundColor: colors.palette.background.default,
                        },
                    },
                },
            ],
            styleOverrides: {
                root: {
                    fontFamily: fonts.primary,
                    fontSize: '1rem',
                    fontWeight: 400,
                    borderRadius: '20px',
                    padding: '8px 24px',
                    textTransform: 'none',
                    '&.surveyCardButton, &.surveyLoadMoreButton': {
                        backgroundColor: 'white',
                        padding: '6px 24px',
                        maxHeight: '40px',
                        color: colors.palette.primary.main,
                        border: `1px solid ${colors.palette.primary.main}`,
                        '&:hover': {
                            color: colors.palette.primary.main,
                            backgroundColor: colors.other.background.highlight,
                        },
                        '&:focus': {
                            backgroundColor: 'rgba(213, 231, 246)',
                            color: colors.palette.primary.main,
                        },
                    },
                    '&.topMenuButton': {
                        whiteSpace: 'nowrap',
                        '&.topMenuButton.selectOpinionTopMenuItem': {
                            display: 'flex',
                            gap: '0.75rem',
                            color: 'white',
                            paddingLeft: '1.25rem',
                            backgroundColor: 'rgba(29, 81, 86, 1)',
                            ':hover': {
                                color: 'white',
                                backgroundColor: 'rgba(74, 116, 120, 1)',
                            },
                        },
                    },
                    '&.ApplyFiltersButton': {
                        height: '40px',
                    },
                    '&.surveyLoadMoreButton': {
                        width: '100%',
                    },
                    '&.Mui-disabled:not(.mobileLocaleButton)': {
                        backgroundColor: 'rgba(243, 243, 243)',
                        color: 'rgba(173, 181, 189)',
                        borderColor: 'rgba(243, 243, 243)',
                    },
                    '&.language-button': {
                        color: colors.palette.text.primary,
                        padding: '4px 11px',
                        '&:hover, &[aria-expanded="true"]': {
                            backgroundColor: colors.other.background.highlight,
                            color: colors.palette.primary.main,
                        },
                        '&:hover': {
                            borderColor: 'transparent',
                            padding: '4px 11px',
                        },
                    },
                },
                containedPrimary: {
                    backgroundColor: colors.palette.primary.main,
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: 'rgba(85, 133, 189, 1)',
                    },
                },
                outlined: {
                    '&.loginButton, &.mobileLoginButton': {
                        border: '0',
                        color: colors.palette.text.primary,
                        '&:hover': {
                            backgroundColor: colors.other.background.highlight,
                            color: colors.palette.primary.main,
                        },
                        '&:focus': {
                            backgroundColor: colors.other.background.highlight,
                        },
                    },
                    '&.logoutButton, &.mobileLogoutButton': {
                        border: '0',
                        color: colors.palette.text.primary,
                        '&:hover': {
                            backgroundColor: colors.other.background.highlight,
                            color: colors.palette.primary.main,
                        },
                        '&:focus': {
                            backgroundColor: colors.other.background.highlight,
                        },
                    },
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    transition: 'all 0.15s',
                    '&:hover': {
                        color: colors.palette.primary.main,
                        background: colors.other.background.highlight,
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                        color: 'white',
                        background: colors.palette.primary.main,
                    },
                },
            },
        },
        MuiBackdrop: defaultTheme.MuiBackdrop,
        MuiBadge: {
            styleOverrides: {
                root: {
                    '&.notificationBadge > .MuiBadge-badge': {
                        right: -8,
                        backgroundColor: colors.other.notifications.warning,
                    },
                },
            },
        },
        MuiTooltip: defaultTheme.MuiTooltip,
        MuiStep: defaultTheme.MuiStep,
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStepIcon.styleOverrides.root,
                    color: colors.other.background.highlight,
                    '&.Mui-active': {
                        color: colors.palette.primary.main,
                        '> text': {
                            fill: 'white',
                        },
                    },
                },
                text: {
                    ...defaultTheme.MuiStepIcon.styleOverrides.text,
                    fill: colors.palette.primary.main,
                    fontWeight: 700,
                    '&.Mui-active': {
                        fill: 'green',
                    },
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                root: defaultTheme.MuiStepLabel.styleOverrides.root,
                vertical: {
                    '.MuiStepLabel-label': {
                        ...defaultTheme.MuiStepLabel.styleOverrides.vertical['.MuiStepLabel-label'],
                        backgroundColor: colors.other.background.highlight,
                        '&.Mui-active': {
                            ...defaultTheme.MuiStepLabel.styleOverrides.vertical[
                                '.MuiStepLabel-label'
                            ]['&.Mui-active'],
                            fontWeight: 700,
                            color: colors.palette.secondary.main,
                        },
                    },
                },
                horizontal: {
                    '.MuiStepLabel-label': {
                        ...defaultTheme.MuiStepLabel.styleOverrides.horizontal[
                            '.MuiStepLabel-label'
                        ],
                        '&.Mui-active': {
                            ...defaultTheme.MuiStepLabel.styleOverrides.horizontal[
                                '.MuiStepLabel-label'
                            ]['&.Mui-active'],
                            fontWeight: 700,
                        },
                        [breakpoints.up(420)]: {
                            '&.Mui-active': {
                                display: 'inline-block',
                            },
                        },
                    },
                },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                root: {
                    '.MuiStepConnector-line': {
                        borderColor: colors.other.background.highlight,
                    },
                },
            },
        },
        MuiDivider: defaultTheme.MuiDivider,
        MuiTabs: {
            styleOverrides: {
                flexContainer: {
                    gap: '4px',
                },
                root: {
                    ...defaultTheme.MuiTabs.styleOverrides.root,
                    backgroundColor: 'white',
                    minHeight: '40px',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: '40px',
                    padding: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    margin: 0,
                    color: colors.palette.text.primary,
                    backgroundColor: 'rgba(115, 165, 216, 0.05)',
                    borderRadius: borderRadius.primary,
                    transition: 'all 0.15s',
                    '&:hover': {
                        ...defaultTheme.MuiTab.styleOverrides.root['&:hover'],
                        backgroundColor: colors.other.background.highlight,
                    },
                    '&.Mui-selected': defaultTheme.MuiTab.styleOverrides.root['&.Mui-selected'],
                    '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(85, 133, 189, 1)',
                    },
                    disabled: {
                        color: 'rgba(173, 181, 189)',
                        background: 'rgba(243, 243, 243)',
                    },
                },
            },
        },
        MuiTabPanel: defaultTheme.MuiTabPanel,
        MuiTableHead: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTableHead.styleOverrides.root,
                    fontSize: '0.75rem',
                    fontWeight: 400,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTableCell.styleOverrides.root,
                    fontSize: '0.875rem',
                },
                head: {
                    ...defaultTheme.MuiTableCell.styleOverrides.head,
                    fontSize: '0.75rem',
                    fontWeight: 400,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTableRow.styleOverrides.root,
                    '.MuiTableCell-root': {
                        padding: padding.tableCellPadding,
                    },
                    '&:hover': {
                        '& > *:not(th)': {
                            color: colors.palette.primary.main,
                        },
                        '& td': {
                            backgroundColor: 'transparent',
                        },
                    },
                    '&:nth-of-type(even)': {
                        '& td': { backgroundColor: colors.other.table.evenRowHighlight },
                        '&:hover td': {
                            backgroundColor: colors.other.table.evenRowHighlight,
                        },
                    },
                },
                head: defaultTheme.MuiTableRow.styleOverrides.head,
            },
        },
        MuiPickersPopper: defaultTheme.MuiPickersPopper,
        MuiPickersDay: defaultTheme.MuiPickersDay,
        MuiSkeleton: defaultTheme.MuiSkeleton,
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    color: colors.palette.text.primary,
                    fontSize: '0.875rem',
                    borderRadius: '50%',
                    marginBottom: '2px',
                    '&:hover': {
                        color: colors.palette.primary.main,
                        backgroundColor: colors.other.background.highlight,
                    },
                    '&.MuiPaginationItem-ellipsis, &.MuiPaginationItem-ellipsis:hover': {
                        color: colors.palette.text.primary,
                        cursor: 'default',
                        backgroundColor: 'transparent',
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                        color: 'white',
                        backgroundColor: colors.palette.primary.main,
                        border: 'none',
                        cursor: 'default',
                    },
                    '&.Mui-disabled': {
                        color: 'rgba(173, 181, 189)',
                        backgroundColor: 'rgba(243, 243, 243)',
                    },
                },
            },
        },
    },
    shadows: Array(25).fill('none') as Shadows,
});

export default theme;
